import { useEffect, useRef, useState } from 'react';
import type {
  AttentionItem,
  WorkspaceProjection,
  WorkspaceResearchData,
} from '../../consumer/command-workspace-contract';
import {
  longitudeSpan,
  projectPoint,
  wrapLongitude,
  type Bbox,
} from '../../consumer/fire-signals/contract';

export function formatRecordTime(raw: string, historical: boolean): string {
  const timestamp = Date.parse(raw);
  if (!Number.isFinite(timestamp)) return '时间未知';
  const utc = new Date(timestamp).toISOString();
  return historical ? `${utc.slice(5, 10)} ${utc.slice(11, 16)} UTC` : `${utc.slice(11, 19)} UTC`;
}
export function historicalDateLabel(items: AttentionItem[]): string {
  const dates = items
    .flatMap((item) => {
      const raw = item.candidate?.satellite.acquiredAtUTC;
      const timestamp = Date.parse(raw ?? '');
      return Number.isFinite(timestamp) ? [new Date(timestamp).toISOString().slice(0, 10)] : [];
    })
    .sort();
  if (!dates.length) return '历史日期待读取';
  return dates[0] === dates[dates.length - 1]
    ? dates[0]!
    : `${dates[0]} — ${dates[dates.length - 1]}`;
}
export function markerLabel(item: AttentionItem): string {
  const seconds = item.kind === 'forecast' ? /未来(\d+)秒/.exec(item.title)?.[1] : undefined;
  return seconds ? `预测 +${seconds} 秒` : KIND_LABELS[item.kind];
}
export const KIND_LABELS = {
  warning: '观测预警',
  forecast: '预测关注',
  linked: '已关联天气',
  unlinked: '未关联天气',
};
export function selectedItem(items: AttentionItem[], id: string): AttentionItem | null {
  return id ? (items.find((item) => item.id === id) ?? null) : (items[0] ?? null);
}
export function AttentionContext({
  item,
  missing,
}: {
  item: AttentionItem | null;
  missing: boolean;
}) {
  return (
    <section
      className="cw-selected"
      data-vs08-selected-id={item?.id ?? ''}
      aria-label="当前选中记录"
      aria-live="polite"
    >
      <span className="cw-eyebrow">当前选中</span>
      {item ? (
        <>
          <h3>{item.title}</h3>
          <p>{item.reason}</p>
          <dl>
            {item.facts.slice(0, 2).map((fact) => (
              <div key={fact.label}>
                <dt>{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
          {item.href ? (
            <a className="cw-primary" data-vs08-detail href={item.href}>
              查看详情 <span aria-hidden="true">→</span>
            </a>
          ) : (
            <span className="cw-muted">暂无可用详情引用</span>
          )}
        </>
      ) : (
        <>
          <h3>{missing ? '选中记录不在当前范围' : '暂无可选记录'}</h3>
          <p>
            {missing
              ? '保留了原始记录身份；请调整范围或选择另一条记录。'
              : '当前结果未知或没有匹配记录；无资料不表示安全。'}
          </p>
        </>
      )}
    </section>
  );
}
export function TopologyCanvas({
  projection,
  selected,
  select,
  scope,
  sources,
}: {
  projection: WorkspaceProjection;
  selected: string;
  select: (id: string) => void;
  scope: (id: string) => void;
  sources: boolean;
}) {
  const topologyRef = useRef<HTMLDivElement>(null);
  const areaKey = projection.areas.map((area) => area.id).join('|');
  useEffect(() => {
    const topology = topologyRef.current;
    const viewport = topology?.closest<HTMLElement>('.cw-canvas-viewport');
    const marker = topology
      ? Array.from(topology.querySelectorAll<HTMLElement>('[data-vs08-select-id]')).find(
          (entry) => entry.dataset.vs08SelectId === selected,
        )
      : null;
    const region = marker?.closest<HTMLElement>('[data-vs08-area-id]');
    if (!viewport || !region) return;
    const view = viewport.getBoundingClientRect(),
      box = region.getBoundingClientRect(),
      title = region.querySelector('header')?.getBoundingClientRect(),
      picked = marker!.getBoundingClientRect();
    // Scroll only this canvas viewport; never scroll the page or hide an area's title above an inaccessible origin.
    if (
      (title?.top ?? box.top) < view.top + 8 ||
      picked.top < view.top + 8 ||
      picked.bottom > view.bottom - 8
    ) {
      const top = Math.max(0, viewport.scrollTop + box.top - view.top - 10);
      viewport.scrollTo({ top, behavior: 'smooth' });
    }
  }, [selected, areaKey]);
  return (
    <div
      ref={topologyRef}
      className="cw-topology"
      role="region"
      aria-label="区域与监测来源关系拓扑，按配置归属排列，不表示实际距离或地理位置"
    >
      <div className="cw-topology-heading">
        <span className="cw-live" aria-label="当前读取状态">
          {projection.current ? '读取有效' : '状态待核对'}
        </span>
      </div>
      <div className="cw-area-grid">
        {projection.areas.map((area) => (
          <article className="cw-area" key={area.id} data-vs08-area-id={area.id}>
            <header>
              <button className="cw-area-title" type="button" onClick={() => scope(area.id)}>
                {area.name} <span aria-hidden="true">↗</span>
              </button>
              <small>{area.coverage}</small>
            </header>
            <div className="cw-area-signals">
              {area.attentionIds.map((id) => {
                const item = projection.items.find((record) => record.id === id);
                return item ? (
                  <button
                    type="button"
                    key={id}
                    data-vs08-select-id={id}
                    data-kind={item.kind}
                    className={`cw-topology-signal cw-${item.kind}`}
                    aria-label={`${item.title} · ${item.status}`}
                    title={`${item.title} · ${item.status}`}
                    aria-pressed={selected === id}
                    onClick={() => select(id)}
                  >
                    <i aria-hidden="true" />
                    <span>
                      <strong>{markerLabel(item)}</strong>
                      <small>{item.status}</small>
                    </span>
                    <span aria-hidden="true">→</span>
                  </button>
                ) : null;
              })}
              {!area.attentionIds.length && <p className="cw-area-empty">当前没有关注记录</p>}
            </div>
            {sources && (
              <div className="cw-source-grid" aria-label={`${area.name} 来源`}>
                {area.sources.map((source) => (
                  <span
                    key={source.id}
                    title={`${source.id} · epoch ${source.epoch}`}
                    data-source-id={source.id}
                    data-status={source.status}
                  >
                    <i aria-hidden="true" />
                    <span>
                      {source.name}
                      <small>
                        {source.status} · epoch {source.epoch}
                      </small>
                    </span>
                  </span>
                ))}
                {!area.sources.length && <span>来源资料未提供</span>}
              </div>
            )}
          </article>
        ))}
      </div>
      {!projection.areas.length && <div className="cw-empty">正在等待区域配置与后端读取结果。</div>}
    </div>
  );
}
interface MarkerGroup {
  x: number;
  y: number;
  items: AttentionItem[];
}
/** Nearby points share an explicit stack, while exact original coordinates remain on each record. */
export function markerGroups(items: AttentionItem[], bbox: Bbox): MarkerGroup[] {
  const groups: MarkerGroup[] = [];
  for (const item of items) {
    if (!item.candidate) continue;
    const point = projectPoint(
      item.candidate.satellite.longitude,
      item.candidate.satellite.latitude,
      bbox,
    );
    if (!point) continue;
    const group = groups.find((entry) => Math.hypot(entry.x - point[0], entry.y - point[1]) < 23);
    if (group) group.items.push(item);
    else groups.push({ x: point[0], y: point[1], items: [item] });
  }
  return groups;
}
export function ResearchCanvas({
  projection,
  data,
  selected,
  select,
  sources,
}: {
  projection: WorkspaceProjection;
  data: WorkspaceResearchData | null;
  selected: string;
  select: (id: string) => void;
  sources: boolean;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const bbox: Bbox = projection.bbox ?? [-180, -90, 180, 90];
  const groups = markerGroups(projection.items, bbox);
  const picked = projection.items.find((item) => item.id === selected)?.candidate;
  const pickedPoint = picked
    ? projectPoint(picked.satellite.longitude, picked.satellite.latitude, bbox)
    : null;
  return (
    <div className="cw-geography">
      <svg
        viewBox="0 0 1000 480"
        role="group"
        data-vs08-bbox={bbox.join(',')}
        aria-label="EPSG:4326 经纬度格网，无底图"
      >
        <title>NASA 像元中心与 NOAA 气象站的真实历史坐标</title>
        <rect x="60" y="30" width="880" height="400" fill="#eff5fa" />
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i}>
            <path
              d={`M${60 + i * 220} 30V430 M60 ${30 + i * 100}H940`}
              stroke="#cbdbe9"
              strokeDasharray="3 5"
            />
            <text x={60 + i * 220} y="451" textAnchor="middle">
              {wrapLongitude(bbox[0] + (longitudeSpan(bbox) * i) / 4).toFixed(2)}°
            </text>
            <text x="51" y={35 + i * 100} textAnchor="end">
              {(bbox[3] - ((bbox[3] - bbox[1]) * i) / 4).toFixed(2)}°
            </text>
          </g>
        ))}
        <text x="500" y="475" textAnchor="middle">
          经度 · EPSG:4326 · 经纬度格网 / 无底图
        </text>
        {sources &&
          data?.summary.stations.map((station) => {
            const point = projectPoint(station.longitude, station.latitude, bbox);
            return point ? (
              <g key={station.stationId} data-station-id={station.stationId}>
                <path
                  d={`M${point[0]} ${point[1] - 10}l10 20h-20z`}
                  fill="#284f7d"
                  stroke="white"
                  strokeWidth="2"
                />
                <text x={Math.min(820, point[0] + 14)} y={point[1] - 10}>
                  {station.name}
                </text>
                <title>{`NOAA ${station.stationId} · ${station.latitude}, ${station.longitude}`}</title>
              </g>
            ) : null;
          })}
        {projection.items.map((item) => {
          const satellite = item.candidate?.satellite;
          const point = satellite
            ? projectPoint(satellite.longitude, satellite.latitude, bbox)
            : null;
          return satellite && point ? (
            <g
              key={item.id}
              role="button"
              tabIndex={0}
              data-candidate-id={item.id}
              data-latitude={satellite.latitude}
              data-longitude={satellite.longitude}
              data-vs08-select-id={item.id}
              aria-label={`${item.title} · ${item.time} · ${satellite.latitude}, ${satellite.longitude}`}
              aria-pressed={selected === item.id}
              onClick={() => select(item.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  select(item.id);
                }
              }}
            >
              <circle
                cx={point[0]}
                cy={point[1]}
                r="4"
                fill={item.kind === 'linked' ? '#219ab0' : '#b38b55'}
                stroke="white"
              />
              <title>{`${item.id} · ${satellite.latitude}, ${satellite.longitude}`}</title>
            </g>
          ) : null;
        })}
        {pickedPoint && (
          <circle
            cx={pickedPoint[0]}
            cy={pickedPoint[1]}
            r="22"
            fill="none"
            stroke="#155dca"
            strokeWidth="3"
          />
        )}
      </svg>
      <div className="cw-marker-layer">
        {groups.map((group) => {
          const first = group.items[0]!;
          const stacked = group.items.length > 1;
          const opened = expanded === first.id;
          return (
            <div
              className="cw-marker-anchor"
              key={first.id}
              style={{ left: `${group.x / 10}%`, top: `${group.y / 4.8}%` }}
            >
              <button
                type="button"
                className={`cw-marker cw-${first.kind}`}
                data-marker-id={stacked ? undefined : first.id}
                data-latitude={first.candidate?.satellite.latitude}
                data-longitude={first.candidate?.satellite.longitude}
                data-vs08-select-id={stacked ? undefined : first.id}
                aria-pressed={group.items.some((item) => item.id === selected)}
                aria-expanded={stacked ? opened : undefined}
                aria-label={
                  stacked
                    ? `选择 ${group.items.length} 个相邻历史候选`
                    : `${first.title} · ${first.time}`
                }
                onClick={() => (stacked ? setExpanded(opened ? null : first.id) : select(first.id))}
              >
                {stacked ? group.items.length : <span aria-hidden="true">●</span>}
              </button>
              {stacked && opened && (
                <div className="cw-marker-stack" role="group" aria-label="相邻像元候选">
                  {group.items.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      data-candidate-id={item.id}
                      data-vs08-select-id={item.id}
                      data-latitude={item.candidate?.satellite.latitude}
                      data-longitude={item.candidate?.satellite.longitude}
                      aria-pressed={selected === item.id}
                      onClick={() => {
                        select(item.id);
                        setExpanded(null);
                      }}
                    >
                      {item.title}
                      <small>{item.time}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <span className="cw-map-label">
        {groups.length
          ? `${projection.items.length} 条候选 · 相邻像元按数字展开`
          : '当前范围没有可绘制候选'}
      </span>
    </div>
  );
}
